import type { Simulation } from '../core/simulation/Simulation';
import type { InputManager } from '../input/InputManager';
import type { GameLoop } from '../systems/GameLoop';
import type { RendererType } from '../core/types/SimulationTypes';
import { RendererFactory } from '../renderers/RendererFactory';
import { SandcastlePresets } from '../core/simulation/SandcastlePresets';

export class UIManager {
  private container: HTMLElement;
  private footer: HTMLElement;
  private simulation: Simulation;
  private inputManager: InputManager;
  private gameLoop: GameLoop;
  private sceneManager: any; // Will be set after construction
  private currentMouseX: number = 0;
  private currentMouseY: number = 0;

  constructor(
    container: HTMLElement,
    simulation: Simulation,
    inputManager: InputManager,
    gameLoop: GameLoop
  ) {
    this.container = container;
    this.footer = document.getElementById('footer')!;
    this.simulation = simulation;
    this.inputManager = inputManager;
    this.gameLoop = gameLoop;
  }

  public setSceneManager(sceneManager: any): void {
    this.sceneManager = sceneManager;
  }

  // Helper to create a button with common properties
  private createButton(
    text: string,
    id: string,
    className: string,
    onClick: () => void
  ): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = className;
    button.textContent = text;
    button.id = id;
    button.onclick = onClick;
    return button;
  }

  // Helper to update info panel value
  private updateInfoValue(elementId: string, value: string): void {
    const row = document.getElementById(elementId);
    if (row) {
      const valueEl = row.querySelector('.value');
      if (valueEl) valueEl.textContent = value;
    }
  }

  public initialize(): void {
    this.createToolSelector();
    this.createRendererSelector();
    this.createPresetButton();
    this.createSimulationControls();
    this.createSandControls();
    this.createWaterControls();
    this.createWaveControls();
    this.createInstructions();

    // Create footer content
    this.createFooter();

    this.updateFooter();
    setInterval(() => this.updateFooter(), 100);

    this.setupMouseTracking();
    this.setupPresetModal();
  }

  private createToolSelector(): void {
    const panel = this.createPanel('Tools');
    panel.id = 'tool-settings-panel';

    const toolSelector = document.createElement('div');
    toolSelector.className = 'tool-selector';

    const sandButton = this.createButton('🏖️ Sand', 'tool-sand', 'tool-button active', () => {
      this.inputManager.setTool('sand');
      this.updateToolButtons();
    });

    const waterButton = this.createButton('💧 Water', 'tool-water', 'tool-button', () => {
      this.inputManager.setTool('water');
      this.updateToolButtons();
    });

    toolSelector.appendChild(sandButton);
    toolSelector.appendChild(waterButton);
    panel.appendChild(toolSelector);

    // Add tool settings dynamically
    this.updateToolSettingsPanel();
  }

  private updateToolButtons(): void {
    const currentTool = this.inputManager.getCurrentTool();
    const sandButton = document.getElementById('tool-sand');
    const waterButton = document.getElementById('tool-water');

    if (sandButton && waterButton) {
      sandButton.className = currentTool === 'sand' ? 'tool-button active' : 'tool-button';
      waterButton.className = currentTool === 'water' ? 'tool-button active' : 'tool-button';
    }

    this.updateToolSettingsPanel();
  }


  private updateToolSettingsPanel(): void {
    const panel = document.getElementById('tool-settings-panel');
    if (!panel) return;

    const currentTool = this.inputManager.getCurrentTool();

    const content = panel.querySelector('.panel-content');
    if (content) {
      content.remove();
    }

    const newContent = document.createElement('div');
    newContent.className = 'panel-content';

    if (currentTool === 'sand') {
      const sandTool = this.inputManager.getSandTool();

      const brushSizeControl = this.createSlider(
        'Brush Size',
        1,
        10,
        sandTool.getBrushSize(),
        '',
        (value) => {
          sandTool.setBrushSize(value);
        },
        1
      );

      const strengthControl = this.createSlider(
        'Strength',
        0.1,
        2.0,
        sandTool.getStrength(),
        'x',
        (value) => {
          sandTool.setStrength(value);
        },
        10
      );

      const modeLabel = document.createElement('div');
      modeLabel.className = 'control-label';
      modeLabel.style.marginTop = '12px';
      modeLabel.style.marginBottom = '8px';
      modeLabel.innerHTML = '<span>Mode</span>';

      const modeButtons = document.createElement('div');
      modeButtons.className = 'tool-selector';

      const raiseButton = this.createButton('⬆️ Raise', 'mode-raise', 'tool-button active', () => {
        sandTool.setMode('raise');
        this.updateModeButtons();
      });

      const lowerButton = this.createButton('⬇️ Lower', 'mode-lower', 'tool-button', () => {
        sandTool.setMode('lower');
        this.updateModeButtons();
      });

      modeButtons.appendChild(raiseButton);
      modeButtons.appendChild(lowerButton);

      newContent.appendChild(brushSizeControl);
      newContent.appendChild(strengthControl);
      newContent.appendChild(modeLabel);
      newContent.appendChild(modeButtons);
    } else if (currentTool === 'water') {
      const waterTool = this.inputManager.getWaterTool();

      const brushSizeControl = this.createSlider(
        'Brush Size',
        1,
        10,
        waterTool.getBrushSize(),
        '',
        (value) => {
          waterTool.setBrushSize(value);
        },
        1
      );

      const strengthControl = this.createSlider(
        'Amount',
        0.5,
        10.0,
        waterTool.getStrength(),
        '',
        (value) => {
          waterTool.setStrength(value);
        },
        10
      );

      newContent.appendChild(brushSizeControl);
      newContent.appendChild(strengthControl);
    }

    panel.appendChild(newContent);
  }

  private updateModeButtons(): void {
    const sandTool = this.inputManager.getSandTool();
    const mode = sandTool.getMode();

    const raiseButton = document.getElementById('mode-raise');
    const lowerButton = document.getElementById('mode-lower');

    if (raiseButton && lowerButton) {
      raiseButton.className = mode === 'raise' ? 'tool-button active' : 'tool-button';
      lowerButton.className = mode === 'lower' ? 'tool-button active' : 'tool-button';
    }
  }

  private createRendererSelector(): void {
    const panel = this.createPanel('Renderer');

    const rendererSelector = document.createElement('div');
    rendererSelector.className = 'tool-selector';
    rendererSelector.style.display = 'grid';
    rendererSelector.style.gridTemplateColumns = '1fr 1fr';
    rendererSelector.style.gap = '8px';

    const rendererTypes: RendererType[] = ['topdown', 'voxel', 'webgl'];

    rendererTypes.forEach((type) => {
      const button = document.createElement('button');
      button.className = type === 'topdown' ? 'tool-button active' : 'tool-button';
      button.textContent = RendererFactory.getDisplayName(type);
      button.id = `renderer-${type}`;
      button.onclick = () => {
        if (this.sceneManager) {
          this.sceneManager.switchRenderer(type);
          this.updateRendererButtons(type);
        }
      };
      rendererSelector.appendChild(button);
    });

    panel.appendChild(rendererSelector);
  }

  private updateRendererButtons(currentRenderer: RendererType): void {
    const rendererTypes: RendererType[] = ['topdown', 'voxel', 'webgl'];

    rendererTypes.forEach((type) => {
      const button = document.getElementById(`renderer-${type}`);
      if (button) {
        button.className = type === currentRenderer ? 'tool-button active' : 'tool-button';
      }
    });
  }

  private createPresetButton(): void {
    const panel = this.createPanel('Sandcastle Presets');

    const button = document.createElement('button');
    button.className = 'button button-primary';
    button.textContent = '🏰 Load Preset...';
    button.onclick = () => {
      document.getElementById('preset-modal')?.classList.add('active');
    };
    panel.appendChild(button);
  }

  private createSimulationControls(): void {
    const panel = this.createPanel('Simulation');

    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'tool-selector';

    const pauseButton = document.createElement('button');
    pauseButton.className = 'tool-button active';
    pauseButton.textContent = '⏸️';
    pauseButton.id = 'pause-button';
    pauseButton.onclick = () => this.togglePause();

    const resetButton = document.createElement('button');
    resetButton.className = 'tool-button';
    resetButton.textContent = '🔄';
    resetButton.onclick = () => this.simulation.reset();

    buttonContainer.appendChild(pauseButton);
    buttonContainer.appendChild(resetButton);
    panel.appendChild(buttonContainer);
  }

  private togglePause(): void {
    if (!this.sceneManager) return;

    const button = document.getElementById('pause-button');

    if (this.sceneManager.isPaused()) {
      this.sceneManager.resume();
      if (button) {
        button.textContent = '⏸️';
        button.className = 'tool-button active';
      }
    } else {
      this.sceneManager.pause();
      if (button) {
        button.textContent = '▶️';
        button.className = 'tool-button';
      }
    }
  }

  private createSandControls(): void {
    const panel = this.createPanel('Sand Physics');

    const angleControl = this.createSlider(
      'Angle of Repose',
      20,
      60,
      this.simulation.getConfig().sand.angleOfRepose,
      '°',
      (value) => {
        this.simulation.getPhysicsEngine().getSandPhysics().setAngleOfRepose(value);
      }
    );

    const collapseControl = this.createSlider(
      'Collapse Speed',
      0.1,
      2.0,
      this.simulation.getConfig().sand.collapseRate,
      'x',
      (value) => {
        this.simulation.getPhysicsEngine().getSandPhysics().setCollapseRate(value);
      },
      100
    );

    const cohesionControl = this.createSlider(
      'Cohesion (Stickiness)',
      0.0,
      2.0,
      this.simulation.getConfig().sand.cohesion,
      '',
      (value) => {
        this.simulation.getPhysicsEngine().getSandPhysics().setCohesion(value);
      },
      100
    );

    panel.appendChild(angleControl);
    panel.appendChild(collapseControl);
    panel.appendChild(cohesionControl);
  }

  private createWaterControls(): void {
    const panel = this.createPanel('Water Physics');

    const flowControl = this.createSlider(
      'Flow Speed',
      0.5,
      5.0,
      this.simulation.getConfig().water.flowSpeed,
      'x',
      (value) => {
        this.simulation.getPhysicsEngine().getWaterPhysics().setFlowSpeed(value);
      },
      100
    );

    const erosionControl = this.createSlider(
      'Erosion Rate',
      0.0,
      0.5,
      this.simulation.getConfig().water.erosionRate,
      'x',
      (value) => {
        this.simulation.getPhysicsEngine().getWaterPhysics().setErosionRate(value);
      },
      100
    );

    panel.appendChild(flowControl);
    panel.appendChild(erosionControl);
  }

  private createWaveControls(): void {
    const panel = this.createPanel('Waves');

    const tideLevelControl = this.createSlider(
      'Tide Level',
      0,
      10,
      this.simulation.getConfig().waves.baseTideLevel,
      '',
      (value) => {
        this.simulation.getPhysicsEngine().getWaveGenerator().setBaseTideLevel(value);
      },
      10
    );

    const waveAmpControl = this.createSlider(
      'Wave Amplitude',
      0,
      3,
      this.simulation.getConfig().waves.waveAmplitude,
      '',
      (value) => {
        this.simulation.getPhysicsEngine().getWaveGenerator().setWaveAmplitude(value);
      },
      100
    );

    panel.appendChild(tideLevelControl);
    panel.appendChild(waveAmpControl);
  }

  private createFooter(): void {
    // Info section
    const infoSection = document.createElement('div');
    infoSection.className = 'footer-section';
    infoSection.innerHTML = `
      <div class="footer-item">
        <span class="footer-label">Physics FPS:</span>
        <span class="footer-value" id="footer-physics-fps">60</span>
      </div>
      <div class="footer-item">
        <span class="footer-label">Render FPS:</span>
        <span class="footer-value" id="footer-render-fps">60</span>
      </div>
      <div class="footer-item">
        <span class="footer-label">Time:</span>
        <span class="footer-value" id="footer-time">0.0s</span>
      </div>
      <div class="footer-item">
        <span class="footer-label">Max Height:</span>
        <span class="footer-value" id="footer-max-height">0</span>
      </div>
      <div class="footer-item">
        <span class="footer-label">Total Water:</span>
        <span class="footer-value" id="footer-total-water">0</span>
      </div>
    `;

    // Hover section
    const hoverSection = document.createElement('div');
    hoverSection.className = 'footer-section';
    hoverSection.style.marginLeft = 'auto';
    hoverSection.innerHTML = `
      <div class="footer-item">
        <span class="footer-label">Cell:</span>
        <span class="footer-value" id="footer-cell">-</span>
      </div>
      <div class="footer-item">
        <span class="footer-label">Sand:</span>
        <span class="footer-value" id="footer-sand">-</span>
      </div>
      <div class="footer-item">
        <span class="footer-label">Water:</span>
        <span class="footer-value" id="footer-water">-</span>
      </div>
      <div class="footer-item">
        <span class="footer-label">Wetness:</span>
        <span class="footer-value" id="footer-wetness">-</span>
      </div>
      <div class="footer-item">
        <span class="footer-label">Velocity:</span>
        <span class="footer-value" id="footer-velocity">-</span>
      </div>
    `;

    this.footer.appendChild(infoSection);
    this.footer.appendChild(hoverSection);
  }

  private updateFooter(): void {
    const physicsFps = document.getElementById('footer-physics-fps');
    const renderFps = document.getElementById('footer-render-fps');
    const time = document.getElementById('footer-time');
    const maxHeight = document.getElementById('footer-max-height');
    const totalWater = document.getElementById('footer-total-water');

    if (physicsFps) physicsFps.textContent = this.gameLoop.getPhysicsFPS().toString();
    if (renderFps) renderFps.textContent = this.gameLoop.getRenderFPS().toString();

    const state = this.simulation.getState();
    if (time) time.textContent = state.time.toFixed(1) + 's';
    if (maxHeight) maxHeight.textContent = state.metadata.maxHeight.toFixed(1);

    // Calculate total water in the grid
    if (totalWater && state.waterGrid) {
      const sum = state.waterGrid.getSum();
      totalWater.textContent = sum.toFixed(1);
    }
  }

  private createInstructions(): void {
    const panel = this.createPanel('Controls');

    const instructions = document.createElement('div');
    instructions.className = 'instructions';
    instructions.innerHTML = `
      <div style="margin-bottom: 8px;"><kbd>1</kbd> Sand tool</div>
      <div style="margin-bottom: 8px;"><kbd>2</kbd> Water tool</div>
      <div style="margin-bottom: 8px;"><kbd>Left Click</kbd> Raise/Add</div>
      <div style="margin-bottom: 8px;"><kbd>Right Click</kbd> Lower sand</div>
      <div style="margin-bottom: 8px;"><kbd>Arrow Keys</kbd> Pan view</div>
      <div style="margin-bottom: 8px;"><kbd>+/-</kbd> Zoom in/out</div>
    `;

    panel.appendChild(instructions);
  }

  private createPanel(title: string): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'panel';

    const heading = document.createElement('h3');
    heading.textContent = title;
    panel.appendChild(heading);

    this.container.appendChild(panel);
    return panel;
  }

  private createSlider(
    label: string,
    min: number,
    max: number,
    initial: number,
    unit: string,
    onChange: (value: number) => void,
    precision: number = 1
  ): HTMLElement {
    const group = document.createElement('div');
    group.className = 'control-group';

    const labelDiv = document.createElement('div');
    labelDiv.className = 'control-label';

    const labelSpan = document.createElement('span');
    labelSpan.textContent = label;

    const valueSpan = document.createElement('span');
    valueSpan.className = 'control-value';
    valueSpan.textContent = initial.toFixed(precision === 1 ? 0 : 2) + unit;

    labelDiv.appendChild(labelSpan);
    labelDiv.appendChild(valueSpan);

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = (min * precision).toString();
    slider.max = (max * precision).toString();
    slider.value = (initial * precision).toString();
    slider.step = '1';

    slider.oninput = () => {
      const value = parseInt(slider.value) / precision;
      valueSpan.textContent = value.toFixed(precision === 1 ? 0 : 2) + unit;
      onChange(value);
    };

    group.appendChild(labelDiv);
    group.appendChild(slider);

    return group;
  }

  private createInfoRow(label: string, value: string): HTMLElement {
    const row = document.createElement('div');
    row.className = 'info-row';

    const labelSpan = document.createElement('span');
    labelSpan.className = 'label';
    labelSpan.textContent = label;

    const valueSpan = document.createElement('span');
    valueSpan.className = 'value';
    valueSpan.textContent = value;

    row.appendChild(labelSpan);
    row.appendChild(valueSpan);

    return row;
  }

  private setupPresetModal(): void {
    const presetGrid = document.getElementById('preset-grid');
    if (!presetGrid) return;

    const presets = SandcastlePresets.getAll();

    presets.forEach((preset) => {
      const card = document.createElement('div');
      card.className = 'preset-card';
      card.innerHTML = `
        <h3>🏰 ${preset.name}</h3>
        <p>${preset.description}</p>
      `;
      card.onclick = () => {
        const state = this.simulation.getState();
        const centerX = Math.floor(state.metadata.gridWidth / 2);
        const centerY = Math.floor(state.metadata.gridHeight / 2);
        SandcastlePresets.apply(preset.name, state.sandGrid, centerX, centerY, 1.0);
        document.getElementById('preset-modal')?.classList.remove('active');
      };
      presetGrid.appendChild(card);
    });
  }

  private setupMouseTracking(): void {
    // Track mouse position on the canvas/renderer
    document.addEventListener('mousemove', (e) => {
      this.currentMouseX = e.clientX;
      this.currentMouseY = e.clientY;
    });

    // Update hover info every 100ms instead of on every mouse move
    setInterval(() => this.updateHoverInfoFromMouse(), 100);
  }

  private updateHoverInfoFromMouse(): void {
    if (!this.sceneManager) {
      this.updateHoverInfo(null);
      return;
    }

    const renderer = this.sceneManager.getRenderer();
    if (!renderer || !renderer.screenToWorld) {
      this.updateHoverInfo(null);
      return;
    }

    const worldCoord = renderer.screenToWorld(this.currentMouseX, this.currentMouseY);
    if (!worldCoord) {
      this.updateHoverInfo(null);
      return;
    }

    // Get cell data
    const state = this.simulation.getState();
    const x = Math.floor(worldCoord.x);
    const y = Math.floor(worldCoord.y);

    if (x < 0 || x >= state.metadata.gridWidth || y < 0 || y >= state.metadata.gridHeight) {
      this.updateHoverInfo(null);
      return;
    }

    const sandHeight = state.sandGrid.get(x, y);
    const waterDepth = state.waterGrid?.getHeight(x, y) || 0;
    const wetness = state.wetSandTracker?.getWetness(x, y) || 0;
    const velocity = state.waterGrid?.getVelocity(x, y) || [0, 0];

    this.updateHoverInfo({
      x,
      y,
      sandHeight,
      waterDepth,
      wetness,
      velocity
    });
  }

  private updateHoverInfo(data: {
    x: number;
    y: number;
    sandHeight: number;
    waterDepth: number;
    wetness: number;
    velocity: [number, number];
  } | null): void {
    const cellEl = document.getElementById('footer-cell');
    const sandEl = document.getElementById('footer-sand');
    const waterEl = document.getElementById('footer-water');
    const wetnessEl = document.getElementById('footer-wetness');
    const velocityEl = document.getElementById('footer-velocity');

    if (!data) {
      if (cellEl) cellEl.textContent = '-';
      if (sandEl) sandEl.textContent = '-';
      if (waterEl) waterEl.textContent = '-';
      if (wetnessEl) wetnessEl.textContent = '-';
      if (velocityEl) velocityEl.textContent = '-';
      return;
    }

    if (cellEl) cellEl.textContent = `(${data.x}, ${data.y})`;
    if (sandEl) sandEl.textContent = data.sandHeight.toFixed(2);
    if (waterEl) waterEl.textContent = data.waterDepth.toFixed(2);
    if (wetnessEl) wetnessEl.textContent = data.wetness.toFixed(2);
    const velMag = Math.sqrt(data.velocity[0] ** 2 + data.velocity[1] ** 2);
    if (velocityEl) velocityEl.textContent = velMag.toFixed(2);
  }
}
